import { useRef } from "react";

interface PhoneFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

function formatPhone(raw: string): string {
  const cleaned = raw.replace(/\D/g, "");
  const digits = cleaned.length === 11 && cleaned.startsWith("1")
    ? cleaned.slice(1)
    : cleaned.slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function PhoneField({
  value,
  onChange,
  placeholder,
  required,
  id,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: PhoneFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const formatted = formatPhone(raw);
    onChange(formatted);

    // Keep cursor in right spot after formatting
    requestAnimationFrame(() => {
      if (inputRef.current) {
        const digits = formatted.replace(/\D/g, "").length;
        const pos = digits <= 3 ? digits + 1 : digits <= 6 ? digits + 3 : digits + 4;
        inputRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  return (
    <input
      ref={inputRef}
      id={id}
      type="tel"
      inputMode="numeric"
      value={value}
      onChange={handleChange}
      placeholder={placeholder ?? "(555) 000-0000"}
      required={required}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      autoComplete="tel"
      className="w-full px-4 py-2.5 rounded-xl bg-surface-input border border-border-default text-content-primary placeholder:text-content-muted focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all text-sm"
    />
  );
}
